import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { getFilteredGestures, categoryHasMatches } from "../../utils/TrainingHelpers";
import { formatGestureForDisplay } from '../../utils/GestureCategories';
import { phraseSets } from '../../utils/PhraseSets';
import '../../styles/components/Training/GestureFilter.css';

const GestureFilter = ({
    showFilterPopup,
    setShowFilterPopup,
    tempFilters,
    setTempFilters,
    selectedFilters,
    setSelectedFilters,
    gestures,
    filterPosition,
    setFilterPosition,
    filterRef,
    page = 'training'
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [collapsedCategories, setCollapsedCategories] = useState(new Set());
    const dragRef = useRef({ startX: 0, startY: 0 });
    const popupRef = useRef(null);
    const itemLabel = page === 'phrase' ? 'phrases' : 'gestures';
    const itemLabelCapitalized = page === 'phrase' ? 'Phrase' : 'Gesture';
    const phraseMap = useMemo(() => {
        const map = {};
        phraseSets.forEach(p => {
            map[p.id] = p.text;
        });
        return map;
    }, []);

    const handleDragStart = (e) => {
        if (!e.target.closest('.filter-popup-header')) return;
        
        setIsDragging(true);
        const rect = popupRef.current.getBoundingClientRect();
        
        dragRef.current = {
            startX: e.clientX - rect.left,
            startY: e.clientY - rect.top
        };
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    const handleDrag = useCallback((e) => {
        if (!isDragging || !popupRef.current) return;
        
        e.preventDefault();
        const x = e.clientX - dragRef.current.startX;
        const y = e.clientY - dragRef.current.startY;
        
        const rect = popupRef.current.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;
        
        setFilterPosition({
            x: Math.min(Math.max(0, x), maxX),
            y: Math.min(Math.max(0, y), maxY)
        });
    }, [isDragging, setFilterPosition]);

    useEffect(() => {
        if (showFilterPopup) {
            document.addEventListener('mousemove', handleDrag);
            document.addEventListener('mouseup', handleDragEnd);
            
            return () => {
                document.removeEventListener('mousemove', handleDrag);
                document.removeEventListener('mouseup', handleDragEnd);
            };
        }
    }, [showFilterPopup, isDragging, handleDrag]);

    useEffect(() => {
        if (showFilterPopup) {
            const filterButton = filterRef.current?.querySelector('.filter-button');
            if (filterButton) {
                const rect = filterButton.getBoundingClientRect();
                const popupWidth = 320;
                
                let x = Math.max(0, rect.left);
                if (x + popupWidth > window.innerWidth) {
                    x = window.innerWidth - popupWidth - 20;
                }
                
                setFilterPosition({ 
                    x,
                    y: rect.bottom + 10
                });
            }
        }
    }, [showFilterPopup, filterRef, setFilterPosition]);

    const handleApplyFilters = () => {
        setSelectedFilters(new Set(tempFilters));
        setShowFilterPopup(false);
    };

    const handleClearFilters = () => {
        setTempFilters(new Set());
        setSelectedFilters(new Set());
        setShowFilterPopup(false);
    };

    const toggleCategory = (categoryId) => {
        setCollapsedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    return (
        <div className="filter-container" ref={filterRef}>
            <button
                className="filter-button"
                onClick={() => setShowFilterPopup(!showFilterPopup)}
                title={`Filter ${itemLabel}`}
            >
                <img src="/icons/filter-icon.svg" alt="Filter" className="icon" />
                {selectedFilters.size > 0 && (
                    <span className="filter-count">{selectedFilters.size}</span>
                )}
            </button>
            {selectedFilters.size > 0 && (
                <button
                    className="clear-filters-button"
                    onClick={handleClearFilters}
                >
                    Clear Filters
                </button>
            )}
            {showFilterPopup && (
                <div 
                    ref={popupRef}
                    className={`filter-popup${isDragging ? ' dragging' : ''}`}
                    style={{
                        left: `${filterPosition.x}px`,
                        top: `${filterPosition.y}px`
                    }}
                    onMouseDown={handleDragStart}
                >
                    <div className="filter-popup-header">
                        <h4>Filter by {itemLabelCapitalized}</h4>
                        <button
                            className="close-filter-button"
                            onClick={() => setShowFilterPopup(false)}
                            title="Close filters"
                        >
                            <img src="/icons/x.svg" alt="Close" className="icon" />
                        </button>
                    </div>
                    <div className="filter-search">
                        <input
                            type="text"
                            placeholder={`Search ${itemLabel}...`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="filter-search-input"
                        />
                        {searchQuery && (
                            <button
                                className="filter-search-clear"
                                onClick={() => setSearchQuery('')}
                                title="Clear search"
                            >
                                <img src="/icons/x.svg" alt="Clear" className="icon" />
                            </button>
                        )}
                    </div>
                    <div className="filter-options">
                        {gestures.map(category => (
                            categoryHasMatches(category, searchQuery, page, phraseMap) && (
                                <div key={category.id} className="filter-category">
                                    <div
                                        className="filter-category-header"
                                        onClick={() => toggleCategory(category.id)}
                                    >
                                        <span className={`collapse-arrow ${collapsedCategories.has(category.id) ? 'collapsed' : ''}`}>
                                            ▼
                                        </span>
                                        <h5>{category.label}</h5>
                                    </div>
                                    <div className={`filter-category-options ${collapsedCategories.has(category.id) ? 'collapsed' : ''}`}>
                                        {getFilteredGestures(category, searchQuery, page, phraseMap).map(gesture => (
                                            <label key={gesture} className="filter-option">
                                                <input
                                                    type="checkbox"
                                                    checked={tempFilters.has(gesture)}
                                                    onChange={() => {
                                                        const newFilters = new Set(tempFilters);
                                                        if (newFilters.has(gesture)) {
                                                            newFilters.delete(gesture);
                                                        } else {
                                                            newFilters.add(gesture);
                                                        }
                                                        setTempFilters(newFilters);
                                                    }}
                                                />
                                                {page === 'phrase' ? (phraseMap[gesture] || gesture) : formatGestureForDisplay(gesture)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )
                        ))}
                        {gestures.every(category => !categoryHasMatches(category, searchQuery, page, phraseMap)) && (
                            <div className="no-results">No matching {itemLabel} found</div>
                        )}
                    </div>
                    {tempFilters.size > 0 && (
                        <button 
                            className="apply-filters-button"
                            onClick={handleApplyFilters}
                        >
                            Apply Filter{tempFilters.size > 1 ? 's' : ''}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default GestureFilter; 